import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { ResultService } from "@core/services/utils/result.service";
import { BePaymentsService } from "./be-payments.service";

@Injectable({
  providedIn: "root",
})
export class PaymentRedirectService {
  private router = inject(Router);
  private paymentsService = inject(BePaymentsService);
  private resultService = inject(ResultService);

  /**
   * Handles payment success redirect
   */
  handlePaymentSuccess(
    recordId: string,
    paymentType = "downpayment",
    paymentId?: string
  ): void {
    if (recordId) {
      this.router.navigate([`/records/${recordId}/overview`], {
        queryParams: {
          payment_result: "success",
          payment_type: paymentType,
          payment_id: paymentId,
        },
      });
    } else {
      console.warn("No recordId found in payment success return");
      this.router.navigate(["/records"]);
    }
  }

  /**
   * Handles payment failure/cancellation redirect with error display
   */
  handlePaymentFailure(
    recordId: string,
    paymentType = "downpayment",
    paymentId?: string,
    errorDetails?: Record<string, unknown>
  ): void {
    // Show error notification directly if error details are provided
    if (errorDetails?.["textId"]) {
      this.resultService.openError(
        this.getErrorMessage(errorDetails["textId"] as string)
      );
    }

    // Redirect to clean URL without error params
    if (recordId) {
      this.router.navigate([`/records/${recordId}/overview`], {
        queryParams: {
          payment_result: "cancelled",
          payment_type: paymentType,
          payment_id: paymentId,
        },
      });
    } else {
      console.warn("No recordId found in payment failed return");
      this.router.navigate(["/records"]);
    }
  }

  /**
   * Get centralized error message for payment errors
   */
  private getErrorMessage(textId: string): string {
    switch (textId) {
      case "LCException.Payment.InProcess":
        return "Payment is currently being processed";
      case "LCException.Payment.NotRequested":
        return "Payment has not been requested";
      case "LCException.PaymentIntent.NotFound":
      case "LCException.CheckoutSession.NotFound":
        return "Payment information not found";
      case "LCException.Payment.AlreadyProcessed":
        return "Payment has already been processed";
      case "LCException.Payment.Expired":
        return "Payment session has expired";
      case "LCException.Payment.InvalidAmount":
        return "Invalid payment amount";
      default:
        return "An error occurred while processing the payment";
    }
  }

  /**
   * Parse backend return URL to extract payment information
   * This method can handle different backend URL formats and extract relevant data
   */
  parseBackendReturnUrl(url: string): {
    recordId?: string;
    paymentType?: string;
    paymentId?: string;
    additionalParams?: Record<string, string>;
  } | null {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);

      // Extract common parameters that backend might include
      const recordId = params.get("recordId") || params.get("record_id");
      const paymentType =
        params.get("paymentType") ||
        params.get("payment_type") ||
        "downpayment";
      const paymentId = params.get("paymentId") || params.get("payment_id");

      // Extract any additional parameters for future extensibility
      const additionalParams: Record<string, string> = {};
      params.forEach((value, key) => {
        if (
          ![
            "recordId",
            "record_id",
            "paymentType",
            "payment_type",
            "paymentId",
            "payment_id",
          ].includes(key)
        ) {
          additionalParams[key] = value;
        }
      });

      return {
        recordId: recordId || undefined,
        paymentType,
        paymentId: paymentId || undefined,
        additionalParams:
          Object.keys(additionalParams).length > 0
            ? additionalParams
            : undefined,
      };
    } catch (error) {
      console.warn("Error parsing backend return URL:", error);
      return null;
    }
  }

  /**
   * Handle generic payment return from backend URL
   * This provides a unified interface for both success and failure scenarios
   */
  async handlePaymentReturn(url: string, isSuccess: boolean): Promise<void> {
    const parsedData = this.parseBackendReturnUrl(url);

    if (!parsedData?.recordId) {
      console.warn(
        "No recordId found in payment return URL, redirecting to dashboard"
      );
      this.router.navigate(["/records"], {
        queryParams: {
          payment_notification: isSuccess ? "success" : "cancelled",
        },
      });
      return;
    }

    if (isSuccess && parsedData.paymentId) {
      try {
        await this.paymentsService.setPaymentProcessing(parsedData.paymentId);
      } catch (error) {
        console.warn("Error setting payment as processing:", error);
      }

      this.handlePaymentSuccess(
        parsedData.recordId!,
        parsedData.paymentType,
        parsedData.paymentId
      );
    } else if (isSuccess) {
      this.handlePaymentSuccess(
        parsedData.recordId!,
        parsedData.paymentType,
        parsedData.paymentId
      );
    } else {
      this.handlePaymentFailure(
        parsedData.recordId!,
        parsedData.paymentType,
        parsedData.paymentId
      );
    }
  }
}
